import router from '@/router';
import { getLoginRoute, getRouteLocale } from '@/router/navigation';
import { isSalesCrmUserId } from '@/router/middleware/salesCrmAccess.middleware';
import { useAuth } from '@/composables/useAuth';
import { getUserState } from '@/firebase';
import { getMerchantOne } from '@/services/merchant.service';
import { getUserRole, getUser } from '@/services/user.service';
import { resetAuthService } from '@/modules/auth/auth.service';
import store from '@/store';
import axiosInstance from '@/services/axios.service';
import { passwordAPI } from '@/api/password';
import { getAuthStoredValue, removeAuthStoredValue } from '@/utils/storage.utils';
import { resetCategoriesServiceState } from '@/services/categories.service';
import { resetAdditivesServiceState } from '@/services/additives.service';
import { getCurrencySign } from '@/utils/currency';
import { clearContractDrafts } from '@/composables/useContractDraft';

const { login: userLogin, logout, uid } = useAuth();

const initialState = () => ({
  errorLogin: null,
  errorPassword: null,
  loyalty: null,
  menuitem: '',
  merchant: null,
  messageSuccessReset: null,
  pointIDS: null,
  pointsNames: null,
  pointsNamesMerchantId: null,
  role: null,
  units: null,
  user: null,
  userid: null,
  first_launch: true,
  isLoading: false,
});

const SESSION_STORAGE_KEYS_TO_RESET = ['stateItems', 'stateAdditiveItems'];
const AUTH_STORAGE_KEYS_TO_RESET = [
  'accessToken',
  'refreshToken',
  'merchantId',
  'tokenExpiresAt',
  'merchants',
  'user',
  'uid',
  'role',
];

const ROOT_SESSION_RESET_ACTIONS = [
  'permissions/clearCache',
  'catalog/resetState',
  'goods/resetState',
  'educationStore/resetState',
  'featureFlags/resetState',
  'moduleConfig/resetState',
  'socket/disconnect',
];

function resetLocalStoreState(commit) {
  commit('RESET_STATE');
  commit('ACCORDION_RESET_STATE');
  commit('BASKET_RESET_STATE');
}

function resetSessionServicesState() {
  resetCategoriesServiceState();
  resetAdditivesServiceState();
}

function clearSessionViewState() {
  SESSION_STORAGE_KEYS_TO_RESET.forEach((key) => {
    sessionStorage.removeItem(key);
  });
  clearContractDrafts();
}

function clearAuthStorageState() {
  AUTH_STORAGE_KEYS_TO_RESET.forEach((key) => {
    removeAuthStoredValue(key);
  });
}

export default {
  state: initialState,
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
    setIsLoading(state, value) {
      state.isLoading = value;
    },
    set_loyalty(state, loyalty) {
      state.loyalty = loyalty;
    },
    set_merchantSlugName(state, merchantSlugName) {
      state.merchant.merchantSlugName = merchantSlugName;
    },
    set_merchantName(state, merchantName) {
      state.merchant.merchantName = merchantName;
    },
    SET_MENUITEM(state, menuitem) {
      state.menuitem = menuitem;
    },
    set_merchant(state, merchant) {
      state.merchant = merchant;
    },
    set_userid(state, userid) {
      state.userid = userid;
    },
    set_role(state, role) {
      state.role = role;
    },
    reset_merchant(state) {
      state.merchant = null;
    },
    reset_userid(state) {
      state.userid = null;
    },
    reset_role(state) {
      state.role = null;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    UPDATE_USER_AVATAR(state, urlAvatar) {
      if (state.user) {
        state.user.urlAvatar = urlAvatar;
      }
    },
    CLEAR_USER(state) {
      state.user = null;
      state.merchant = null;
      state.pointsNames = null;
      state.pointsNamesMerchantId = null;
      state.role = null;
    },
    SET_ERROR_LOGIN(state, errorLogin) {
      state.errorLogin = errorLogin;
    },
    SET_ERROR_RESET(state, errorLogin) {
      state.errorLogin = errorLogin;
    },

    SET_ERROR_PASSWORD(state, errorPassword) {
      state.errorPassword = errorPassword;
    },
    SET_MESSAGE_FOR_SUCCESS_RESET(state, messageSuccessReset) {
      state.messageSuccessReset = messageSuccessReset;
    },
    CLEAR_MESSAGE_FOR_SUCCESS_RESET(state) {
      state.messageSuccessReset = null;
    },
    set_currencySign(state, currencySign) {
      if (!state.merchant) {
        state.merchant = {};
      }
      state.merchant.currencySign = currencySign;
    },
    set_units(state, units) {
      state.units = units;
    },
    set_first_launch(state, value) {
      state.first_launch = value;
    },
    set_pointsNames(state, payload) {
      state.pointsNames = payload;
    },
    set_pointsNamesMerchantId(state, id) {
      state.pointsNamesMerchantId = id;
    },
  },
  actions: {
    async resetSessionState({ commit, dispatch }) {
      resetLocalStoreState(commit);
      await Promise.all(ROOT_SESSION_RESET_ACTIONS.map((action) => dispatch(action, undefined, { root: true })));
      commit('socket/RESET_STATE', null, { root: true });
      resetSessionServicesState();
      clearSessionViewState();
    },
    SET_ISLOADING({ commit }, value) {
      commit('setIsLoading', value);
    },

    SET_ERROR_LOGIN({ commit }, errorLogin) {
      commit('SET_ERROR_LOGIN', errorLogin);
    },
    setMenuitem({ commit }, details) {
      commit('SET_MENUITEM', details);
    },
    async login({ state, commit, dispatch }, details) {
      const { email, password, isRememberMe } = details;
      await dispatch('resetSessionState');
      dispatch('SET_ISLOADING', true);
      try {
        const auth = await userLogin({ email, password }, isRememberMe);
        console.log('****====>>> auth', auth);

        if (!auth?.pending) localStorage.setItem('qbeek:email', email);

        if (auth?.flow === 'crm' && !auth.pending) {
          const locale = getRouteLocale(router.currentRoute?.value);
          await router.replace({ name: 'sales-crm:clients', params: { locale } });
        } else if (auth?.pending) {
          return true;
        } else {
          const locale = getRouteLocale(router.currentRoute?.value);

          if (isSalesCrmUserId(uid.value)) {
            await router.replace({ name: 'sales-crm:clients', params: { locale } });
          } else if (state?.role === 'back-office') {
            router.replace({ name: 'backoffice:clients' });
          } else {
            console.log('login:merchant', 'main');
            await dispatch('loyaltyStatus/fetchStatus', undefined, { root: true });
            await router.replace({ name: 'main', params: { locale } });
          }
        }

        return true;
      } catch (error) {
        console.log('login:error:***===>>>', error);
        const errorMessage = [401, 423].includes(error?.response?.status)
          ? 'login.errors.accessRestricted'
          : typeof error === 'string' ? error : error.message || 'Проверьте свой логин или пароль';
        commit('SET_ERROR_LOGIN', errorMessage);
        return false;
      } finally {
        dispatch('SET_ISLOADING', false);
      }
    },
    async logout({ commit, dispatch }) {
      const { logout } = useAuth();
      const logoutPromise = logout();
      await router.replace(getLoginRoute());
      await logoutPromise;
      clearAuthStorageState();
      await dispatch('resetSessionState');
      dispatch('employeeAnalytics/resetState', undefined, { root: true });
      dispatch('hallsTables/invalidateCache', undefined, { root: true });
      resetAuthService();
    },
    async reset_password({ commit }, details) {
      try {
        commit('SET_ERROR_LOGIN', null);
        const response = await passwordAPI.requestReset(details.email);
        if (response.success) {
          commit('SET_ERROR_LOGIN', null);
        }
        commit('SET_MESSAGE_FOR_SUCCESS_RESET', response.message || 'OTP отправлен на email.');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Ошибка при сбросе пароля';
        commit('SET_ERROR_LOGIN', errorMessage);
      }
    },

    async verify_otp({ commit }, details) {
      try {
        commit('SET_ERROR_LOGIN', null);
        const response = await passwordAPI.resetPassword(details.email, details.otp);
        commit('SET_MESSAGE_FOR_SUCCESS_RESET', response.message || 'Пароль успешно сброшен');
        console.log('reset_password:response', response);

        return { success: true, data: response };
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Код неверный';
        commit('SET_ERROR_LOGIN', errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    async get_merchant({ commit, dispatch }) {
      const user = await getUserState();
      const userDb = await getUser(['firstName', 'lastName', 'email', 'phones', 'point']);

      const merchant = await getMerchantOne(/* user.uid */);
      const role = await getUserRole();
      if (user) {
        commit('set_merchant', merchant);
        dispatch('moduleConfig/hydrateFromMerchant', merchant, { root: true });
        dispatch('merchantCurrency/setCurrency', merchant?.currencySign || null, { root: true });
        commit('set_userid', user.uid);
        commit('set_role', role);
        commit('SET_USER', userDb);
      } else {
        commit('reset_merchant');
        commit('reset_userid');
        commit('reset_role');
      }
    },
    get_units({ commit }, units) {
      commit('set_units', units);
    },
    SET_FIRST_LAUNCH({ commit }, value) {
      commit('set_first_launch', value);
    },
  },
  getters: {
    getIsLoading(state) {
      return state.isLoading;
    },
    get_menuitem(state) {
      return state.menuitem;
    },
    errorPassword(state) {
      return state.errorPassword;
    },
    errorLogin(state) {
      return state.errorLogin;
    },
    messageForgotSuccess(state) {
      return state.messageSuccessReset;
    },
    merchant(state) {
      return state.merchant;
    },
    userid(state) {
      return state.userid;
    },
    user(state) {
      return state.user;
    },
    role(state) {
      return state.role;
    },
    pointIDS(state) {
      return state.user?.point?.map((p) => p.id);
    },
    merchantSlugName(state) {
      return state.merchant?.merchantSlugName;
    },
    merchantId(state) {
      return state.merchant?.merchantId || getAuthStoredValue('merchantId');
    },
    currencySymbol(state) {
      return getCurrencySign(state.merchant?.currencySign);
    },
    currencySign(state) {
      return state.merchant?.currencySign;
    },
    loyalty(state) {
      return state.loyalty;
    },
    units(state) {
      return state.merchant?.units;
    },
    get_first_launch(state) {
      return state.first_launch;
    },
    pointsNames(state) {
      return state.pointsNames;
    },
    pointsNamesMerchantId(state) {
      return state.pointsNamesMerchantId;
    },
  },
};
